import React from "react";
import PropTypes from "prop-types";
import { Button, Badge, Banner, Text, LegacyCard as Card, Layout, List, InlineStack } from "@shopify/polaris";

import cms from "app/plans/modules/operator/features/cms/cms";

const propTypes = {
  onSelect: PropTypes.func.isRequired,
  plans: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedPlan: PropTypes.string,
};

const defaultProps = {
  selectedPlan: "",
};

const free = "free";

const Plan = (props) => {
  const { cms: cmsContent, isFreePlanMsg, onSelect } = props;
  const getFreePlan = () => {
    const { plans = [] } = props;
    const freePlan = plans.find((plan) => plan.code === free);
    return freePlan;
  };

  const selectFreePlan = () => {
    onSelect(getFreePlan());
  };

  const renderFreePlan = (selectedPlan = "") => {
    const plan = getFreePlan();
    if (!plan || plan.isHidden) {
      return null;
    }
    return (
      <Layout.Section>
        <center>
          <Text variant="headingMd" as="h2">
            {cmsContent.message.notSureTryFreeVersion}
          </Text>
          <Text variant="bodySm" as="p">
            {cmsContent.message.getMarketcubeFree}
          </Text>
          <br />
          <Button
            disabled={selectedPlan === plan.code}
            onClick={() => selectFreePlan()}
            loading={selectedPlan === plan.code}
            size="slim"
            variant="monochromePlain"
          >
            {cmsContent.label.startWithOurFreeVersion}
          </Button>
        </center>
      </Layout.Section>
    );
  };

  const renderTitle = (plan) => {
    const capitalizeName = plan.name.toUpperCase();
    return (
      <InlineStack>
        {`${capitalizeName}`}
        &nbsp;
        {plan.code === "growth" ? <Badge status="success">{cmsContent.label.popular}</Badge> : ""}
        {plan.currentPlan && <Badge status="info">{cmsContent.label.active}</Badge>}
      </InlineStack>
    );
  };

  const renderPlanButton = (plan, selectedPlan = "") => {
    return (
      <Button
        disabled={selectedPlan === plan.code}
        onClick={() => onSelect(plan)}
        variant="primary"
        fullWidth
        loading={selectedPlan === plan.code}
      >
        {cmsContent.label.startForFree}
      </Button>
    );
  };

  const renderListItem = (item) => (
    <List.Item key={item}>
      <Text as="span">{item}</Text>
    </List.Item>
  );

  const getValuesFromObject = (obj = {}) => Object.values(obj);

  const renderCommonFeatures = () => getValuesFromObject(cms.label.commonFeatures).map(renderListItem);

  const renderGrowthPlanFeatures = () => getValuesFromObject(cms.label.growth).map(renderListItem);

  const renderProPlanFeatures = () => getValuesFromObject(cms.label.pro).map(renderListItem);

  const renderCustomPlanFeatures = () => getValuesFromObject(cms.label.customPlan).map(renderListItem);

  const renderBodySection = (plan, selectedPlan) => (
    <Card.Section title={`${cmsContent.label.features}:`}>
      <List>
        {plan.maxProducts && (
          <List.Item>
            <Text as="span">{cmsContent.label.productPlural}</Text>
            {`: ${plan.maxProducts}`}
          </List.Item>
        )}
        {plan.maxVendors && (
          <List.Item>
            <Text as="span">{cmsContent.label.vendorListData}</Text>
            {`: ${plan.maxVendors}`}
          </List.Item>
        )}
        {renderCommonFeatures()}
        {plan.code === "growth" && renderGrowthPlanFeatures()}
        {plan.code === "pro" && renderProPlanFeatures()}
        {plan.code === "customPlan" && renderCustomPlanFeatures()}
      </List>
      <br />
      <InlineStack>
        <Text as="h5">{renderPlanButton(plan, selectedPlan)}</Text>
      </InlineStack>
    </Card.Section>
  );

  const renderHeadingSection = (plan) => (
    <Card.Section>
      <InlineStack blockAlign="center" wrap={false}>
        <div>
          <Text variant="headingSm" as="h1">
            {renderTitle(plan)}
          </Text>
          <Text variant="bodySm" as="p">
            {cmsContent.label.freeTrial}
          </Text>
        </div>
        <Text variant="heading2xl" as="p">{`$${plan.monthlyFee}/m`}</Text>
      </InlineStack>
      {plan.message && <Badge status="warning">{plan.message}</Badge>}
    </Card.Section>
  );

  const renderPlanCard = (plan, selectedPlan) => {
    if (!plan) {
      return null;
    }
    const { _id: planId } = plan;
    return (
      <Layout.Section key={plan.code} oneThird>
        <Card key={`renderShopifyPlanCard${planId}`} className="Polaris-Page__AppInstall">
          {renderHeadingSection(plan)}
          {renderBodySection(plan, selectedPlan)}
        </Card>
      </Layout.Section>
    );
  };

  const renderPlans = (selectedPlan = "") => {
    const { plans = [] } = props;
    const filteredPlans = plans.filter((plan) => plan.code !== free);
    return filteredPlans.map((plan) => renderPlanCard(plan, selectedPlan));
  };

  const { selectedPlan = "" } = props;
  return (
    <>
      {isFreePlanMsg && <Banner title={cms.message.planNote} status="success" />}
      <br />
      <Layout>{renderPlans(selectedPlan)}</Layout>
      <br />
      <br />
      <Layout>{renderFreePlan(selectedPlan)}</Layout>
    </>
  );
};

Plan.propTypes = propTypes;
Plan.defaultProps = defaultProps;

export default Plan;
